import * as React from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, helperText, id, type = 'text', ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const isPassword = type === 'password';
    const [showPassword, setShowPassword] = React.useState(false);

    const resolvedType = isPassword && showPassword ? 'text' : type;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-xs font-medium text-raios-text-secondary mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <input
            id={inputId}
            type={resolvedType}
            ref={ref}
            className={cn(
              // text-base no mobile (16px): fonte <16px em input dispara AUTO-ZOOM
              // no iOS Safari/PWA — era o "login com zoom além da viewport".
              'flex w-full bg-raios-card border border-raios-border rounded-md text-base sm:text-sm text-raios-text placeholder:text-raios-muted h-10 px-3 min-h-[44px] sm:min-h-0 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 focus-visible:border-raios-accent',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              error && 'border-raios-danger focus-visible:ring-raios-danger/40 focus-visible:border-raios-danger',
              isPassword && 'pr-10',
              className
            )}
            aria-invalid={error ? true : undefined}
            aria-describedby={
              error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
            }
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-0 top-0 h-full px-3 flex items-center text-raios-muted hover:text-raios-text transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-raios-accent/40 rounded-r-md"
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Eye className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="mt-1.5 text-xs text-raios-danger-text">
            {error}
          </p>
        )}
        {!error && helperText && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-xs text-raios-muted">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
export type { InputProps };
